import { QueryClient } from '@tanstack/react-query';
import { applyWebSocketEvent, WebSocketEvent } from '@/lib/treeCache';
import { TreeSnapshot } from '@shared/projectTreeDto';
import { apiRequest } from '@/lib/queryClient';
import { invalidateLinkedShareProjectionQueries, isLinkedShareProjectionDelta } from '@/lib/linked-share-cache';

/**
 * Delta Payload Interface
 * Represents a single sequenced delta event from the server
 */
export interface DeltaPayload {
  sequenceNumber: number;
  entityType: string;
  entityId: string | number;
  mutationType: 'create' | 'update' | 'delete';
  timestamp: number;
  data: any;
  meta?: {
    projectId?: string;
    pulseId?: number;
    /** Persisted, identity-free dependency refresh instruction. */
    treeProjectionChanged?: boolean;
    [key: string]: any;
  };
}

/**
 * Delta Manager Statistics
 */
export interface DeltaManagerStats {
  lastAppliedSequence: number;
  pendingCount: number;
  totalApplied: number;
  gapsDetected: number;
  duplicatesDropped: number;
  recoveryAttempts: number;
  lastRecoveryTimestamp: number | null;
}

/**
 * Snapshot Recovery Response from server
 */
interface SnapshotRecoveryResponse {
  snapshot: TreeSnapshot;
  sequenceNumber: number;
  deltas?: DeltaPayload[];
  requiresFullSync?: boolean;
}

/**
 * Delta Processing Result
 */
export type DeltaProcessResult = 'applied' | 'queued' | 'duplicate' | 'gap_detected' | 'skipped';

/**
 * DeltaManager Class
 * 
 * Manages sequenced delta events from WebSocket with:
 * - Gap detection and automatic recovery
 * - Out-of-order delta handling via pending queue
 * - Watchdog timer for stale delta detection
 * - Exponential backoff retry logic
 * - Comprehensive error handling and logging
 */
class DeltaManager {
  private lastAppliedSequence: number = 0;
  private pendingQueue: Map<number, DeltaPayload> = new Map();
  private isSyncing: boolean = false;
  private gapRecoveryInProgress: boolean = false;
  
  // Statistics
  private stats: DeltaManagerStats = {
    lastAppliedSequence: 0,
    pendingCount: 0,
    totalApplied: 0,
    gapsDetected: 0,
    duplicatesDropped: 0,
    recoveryAttempts: 0,
    lastRecoveryTimestamp: null,
  };
  
  // Watchdog timer
  private watchdogTimer: number | null = null;
  private watchdogTimeout: number = 3000; // 3 seconds default
  private lastDeltaTimestamp: number = Date.now();
  private expectedNextSequence: number | null = null;
  
  // Retry configuration
  private maxRetries: number = 3;
  private retryCount: number = 0;
  private retryDelays: number[] = [1000, 2000, 4000]; // Exponential backoff in ms
  
  // Query client reference (set during initialization)
  private queryClient: QueryClient | null = null;
  
  /**
   * Initialize the DeltaManager with a QueryClient instance
   */
  initialize(queryClient: QueryClient): void {
    this.queryClient = queryClient;
  }
  
  /**
   * Bootstrap the DeltaManager with the initial sequence number from server
   * This ensures we start from the correct sequence instead of 0
   * 
   * @param sequenceNumber - The current sequence number from the server snapshot
   */
  bootstrap(sequenceNumber: number): void {
    if (this.lastAppliedSequence === 0) {
      this.lastAppliedSequence = sequenceNumber;
      this.stats.lastAppliedSequence = sequenceNumber;
      
      // Process any deltas that arrived before bootstrap
      if (this.pendingQueue.size > 0) {
        this.drainConsecutivePendingDeltas();
      }
    }
  }
  
  /**
   * Reconcile the cursor with a freshly fetched snapshot's sequence number.
   * Monotonic: only advances, never regresses. Needed because query
   * invalidations refetch the snapshot after bootstrap — without advancing,
   * the next live delta would be misread as a gap (or dropped as a
   * duplicate) relative to a stale cursor. The snapshot sequence is a
   * LOWER bound (stamped read-before-compute), so advancing to it can at
   * worst cause an idempotent delta replay, never a skipped delta.
   */
  reconcileSnapshotSequence(sequenceNumber: number): void {
    if (this.lastAppliedSequence > 0 && sequenceNumber > this.lastAppliedSequence) {
      this.lastAppliedSequence = sequenceNumber;
      this.stats.lastAppliedSequence = sequenceNumber;
    }
  }

  /**
   * Check if the DeltaManager has been bootstrapped
   * 
   * @returns true if bootstrapped (lastAppliedSequence > 0)
   */
  isBootstrapped(): boolean {
    return this.lastAppliedSequence > 0;
  }
  
  /**
   * Process an incoming delta
   * 
   * @param delta - The delta payload to process
   * @returns Processing result status
   */
  processDelta(delta: DeltaPayload): DeltaProcessResult {
    const { sequenceNumber } = delta;
    
    // Update last delta timestamp
    this.lastDeltaTimestamp = Date.now();
    
    // Case 0: Not bootstrapped yet - queue for later processing
    if (!this.isBootstrapped()) {
      this.pendingQueue.set(sequenceNumber, delta);
      this.stats.pendingCount = this.pendingQueue.size;
      return 'queued';
    }
    
    // Case 1: Duplicate or old delta (seq <= lastApplied)
    if (sequenceNumber <= this.lastAppliedSequence) {
      this.stats.duplicatesDropped++;
      return 'duplicate';
    }
    
    // Case 2: Expected next delta (seq === lastApplied + 1)
    if (sequenceNumber === this.lastAppliedSequence + 1) {
      const applied = this.applyDelta(delta);
      this.drainConsecutivePendingDeltas();
      this.resetWatchdog();
      return applied ? 'applied' : 'skipped';
    }
    
    // Case 3: Out-of-order delta with gap (seq > lastApplied + 1)
    console.warn('[DeltaManager] GAP DETECTED:', {
      expected: this.lastAppliedSequence + 1,
      received: sequenceNumber,
      gap: sequenceNumber - this.lastAppliedSequence - 1
    });
    
    this.stats.gapsDetected++;
    this.pendingQueue.set(sequenceNumber, delta);
    this.stats.pendingCount = this.pendingQueue.size;
    
    // Trigger gap recovery
    this.triggerGapRecovery(this.lastAppliedSequence + 1, sequenceNumber);
    
    return 'gap_detected';
  }
  
  /**
   * Apply a single delta to the cache
   * @returns true if delta was applied, false if skipped
   */
  private applyDelta(delta: DeltaPayload): boolean {
    if (!this.queryClient) {
      console.error('[DeltaManager] Cannot apply delta: QueryClient not initialized');
      return false;
    }
    
    try {
      // Recovery checkpoints are deliberately identity/content-free. They
      // consume global sequences without pretending to patch an entity.
      // Only the final checkpoint requests a refresh (in finally below).
      if (delta.meta?.recoveryCheckpoint === true) {
        this.lastAppliedSequence = delta.sequenceNumber;
        this.stats.lastAppliedSequence = delta.sequenceNumber;
        this.stats.totalApplied++;
        return true;
      }
      // Convert DeltaPayload to WebSocketEvent format for treeCache
      const wsEvent: WebSocketEvent = this.convertDeltaToWebSocketEvent(delta);
      
      // Apply to tree snapshot cache WITH updated sequence number
      const applied = this.applyDeltaToCache(wsEvent, this.queryClient, delta.sequenceNumber);
      
      if (applied || isLinkedShareProjectionDelta(delta)) {
        // An identity-free dependency event (or filtered/deleted pulse) has
        // no patchable row. Processing its refresh instruction still consumes
        // this sequence, avoiding a replay/watchdog gap loop.
        // Track successfully patched rows or consumed dependency instructions.
        this.lastAppliedSequence = delta.sequenceNumber;
        this.stats.lastAppliedSequence = delta.sequenceNumber;
        this.stats.totalApplied++;
        return true;
      } else {
        console.log('[DeltaManager] Delta skipped - not applicable to tree cache');
        return false;
      }
      
    } catch (error) {
      console.error('[DeltaManager] Error applying delta:', error, delta);
      // On application failure, trigger recovery
      this.triggerGapRecovery(this.lastAppliedSequence, delta.sequenceNumber);
      return false;
    } finally {
      // Shared by live application, pre-bootstrap queues and gap replay.
      // Run AFTER cache writes: setQueryData would otherwise clear stale flags.
      // A self/session echo cannot skip dependent rows we didn't optimistically
      // change, and an absent snapshot still needs full-row queries refreshed.
      if (isLinkedShareProjectionDelta(delta)) {
        invalidateLinkedShareProjectionQueries(this.queryClient);
      }
    }
  }
  
  /**
   * Convert DeltaPayload to WebSocketEvent format
   */
  private convertDeltaToWebSocketEvent(delta: DeltaPayload): WebSocketEvent {
    const { entityType, mutationType, data, meta } = delta;
    const { treeProjectionChanged: _metaMarker, ...cacheMeta } = meta || {};
    // The dependency marker is an instruction to refetch, not pulse data.
    let cacheData = data;
    if (isLinkedShareProjectionDelta(delta)) {
      const { treeProjectionChanged: _marker, ...unmarkedData } = data || {};
      cacheData = unmarkedData;
      if (data?.update && typeof data.update === 'object') {
        const { treeProjectionChanged: _updateMarker, ...unmarkedUpdate } = data.update;
        cacheData = { ...cacheData, update: unmarkedUpdate };
      }
    }
    
    // Map entity type and mutation to WebSocket event type
    let eventType = '';
    
    if (entityType === 'pulse') {
      eventType = mutationType === 'create' ? 'pulse.created' :
                  mutationType === 'delete' ? 'pulse.deleted' : 'pulse.updated';
    } else if (entityType === 'task') {
      eventType = mutationType === 'create' ? 'task.created' :
                  mutationType === 'delete' ? 'task.deleted' : 'task.updated';
    } else if (entityType === 'project') {
      eventType = mutationType === 'create' ? 'project.created' :
                  mutationType === 'delete' ? 'project.deleted' : 'project.updated';
    } else if (entityType === 'progress') {
      eventType = 'progress.updated';
    } else if (entityType === 'sequence') {
      eventType = 'sequence.updated';
    }
    
    return {
      type: eventType,
      data: {
        ...cacheData,
        ...cacheMeta,
      }
    };
  }
  
  /**
   * Find the active tree query key from the cache
   * Handles parameterized query keys like ['/api/tree/snapshot', { yearId, includePulses }]
   * @returns The active query key or null if not found
   */
  private findActiveTreeQueryKey(queryClient: QueryClient): readonly unknown[] | null {
    const snapshotQueries = queryClient.getQueryCache().findAll({ queryKey: ['/api/tree/snapshot'] });
    
    if (snapshotQueries.length === 0) {
      return null;
    }
    
    // If there's only one query, use it
    if (snapshotQueries.length === 1) {
      return snapshotQueries[0].queryKey;
    }
    
    // Multiple queries exist - prioritize the active one with observers
    // This handles cases where users switch years/filters and multiple snapshots exist
    const activeQuery = snapshotQueries.find(q => q.getObserversCount() > 0);
    
    if (activeQuery) {
      return activeQuery.queryKey;
    }
    
    // Fallback: use the most recently updated query
    const sortedByUpdate = [...snapshotQueries].sort((a, b) => {
      const aTime = a.state.dataUpdatedAt || 0;
      const bTime = b.state.dataUpdatedAt || 0;
      return bTime - aTime; // Descending order (most recent first)
    });
    
    return sortedByUpdate[0].queryKey;
  }
  
  /**
   * Apply delta to React Query cache
   * @returns true if delta was applied, false if skipped
   */
  private applyDeltaToCache(event: WebSocketEvent, queryClient: QueryClient, newSequenceNumber: number): boolean {
    // Find ALL active snapshot queries, not just the base one
    const snapshotQueries = queryClient.getQueryCache().findAll({ 
      queryKey: ['/api/tree/snapshot'], 
      exact: false 
    });

    if (!snapshotQueries.length) {
      return false; // No tree cache
    }

    let appliedToAtLeastOne = false;

    // Iterate and update ALL matching snapshot caches
    for (const query of snapshotQueries) {
      const activeQueryKey = query.queryKey;
      const wrappedData = queryClient.getQueryData<{ snapshot: TreeSnapshot; sequenceNumber: number }>(activeQueryKey);

      if (!wrappedData?.snapshot) {
        continue;
      }

      const updatedSnapshot = applyWebSocketEvent(wrappedData.snapshot, event);

      if (updatedSnapshot && updatedSnapshot !== wrappedData.snapshot) {
        queryClient.setQueryData(activeQueryKey, {
          snapshot: updatedSnapshot,
          sequenceNumber: newSequenceNumber
        });
        appliedToAtLeastOne = true;
      }
    }

    return appliedToAtLeastOne;
  }
  
  /**
   * Drain consecutive pending deltas from the queue
   */
  private drainConsecutivePendingDeltas(): void {
    let nextSeq = this.lastAppliedSequence + 1;
    
    while (this.pendingQueue.has(nextSeq)) {
      const delta = this.pendingQueue.get(nextSeq)!;
      this.pendingQueue.delete(nextSeq);
      
      this.applyDelta(delta);
      
      nextSeq++;
    }
    
    this.stats.pendingCount = this.pendingQueue.size;
    
    // Update stats
    // Pending deltas will be processed when gap is filled
  }
  
  /**
   * Trigger gap recovery process
   */
  private async triggerGapRecovery(expectedSeq: number, receivedSeq: number): Promise<void> {
    if (this.gapRecoveryInProgress) {
      return;
    }
    
    this.gapRecoveryInProgress = true;
    
    try {
      await this.recoverFromGap(expectedSeq, receivedSeq);
      this.retryCount = 0; // Reset retry count on success
    } catch (error) {
      console.error('[DeltaManager] Gap recovery failed:', error);
      
      // Retry with exponential backoff
      if (this.retryCount < this.maxRetries) {
        const delay = this.retryDelays[this.retryCount] || 4000;
        this.retryCount++;
        
        
        setTimeout(() => {
          this.gapRecoveryInProgress = false;
          this.triggerGapRecovery(expectedSeq, receivedSeq);
        }, delay);
      } else {
        console.error('[DeltaManager] Max retries reached, gap recovery failed permanently');
        this.gapRecoveryInProgress = false;
        this.retryCount = 0;
        
        // Alert user or trigger full refresh
        this.alertUserOfSyncFailure();
      }
    }
  }
  
  /**
   * Recover from gap by fetching snapshot with deltas
   */
  async recoverFromGap(expectedSeq: number, receivedSeq: number): Promise<void> {
    console.log('[DeltaManager] 🔄 Starting gap recovery:', {
      expected: expectedSeq,
      received: receivedSeq,
      gap: receivedSeq - expectedSeq
    });
    
    this.isSyncing = true;
    this.stats.recoveryAttempts++;
    this.stats.lastRecoveryTimestamp = Date.now();
    
    if (!this.queryClient) {
      throw new Error('QueryClient not initialized');
    }
    
    try {
      // Fetch snapshot with deltas since last applied sequence
      const url = `/api/tree/snapshot?since=${this.lastAppliedSequence}`;
      const response = await apiRequest('GET', url);
      const recoveryData: SnapshotRecoveryResponse = await response.json();
      
      console.log('[DeltaManager] Recovery data received:', {
        sequenceNumber: recoveryData.sequenceNumber,
        deltaCount: recoveryData.deltas?.length || 0,
        hasSnapshot: !!recoveryData.snapshot,
        requiresFullSync: recoveryData.requiresFullSync
      });
      
      // Strategy 1: Full sync required (gap too large or no deltas available)
      if (recoveryData.requiresFullSync && recoveryData.snapshot) {
        console.log('[DeltaManager] Full sync required - replacing entire snapshot');
        
        // Find the active tree query key (handles parameterized keys)
        const activeQueryKey = this.findActiveTreeQueryKey(this.queryClient);
        
        if (activeQueryKey) {
          // Wrap snapshot in the same structure as normal queries
          this.queryClient.setQueryData(activeQueryKey, {
            snapshot: recoveryData.snapshot,
            sequenceNumber: recoveryData.sequenceNumber
          });
        } else {
          console.warn('[DeltaManager] No active tree query found - cannot update cache');
        }
        
        this.lastAppliedSequence = recoveryData.sequenceNumber;
        this.stats.lastAppliedSequence = recoveryData.sequenceNumber;
        // Full-sync responses can collapse dependency-changing deltas. Other
        // full-row caches (and other snapshot variants) must converge too.
        invalidateLinkedShareProjectionQueries(this.queryClient);
        
        console.log(`[DeltaManager] ✓ Full sync completed at sequence ${recoveryData.sequenceNumber}`);
      }
      // Strategy 2: Incremental recovery with deltas
      else if (recoveryData.deltas && recoveryData.deltas.length > 0) {
        console.log('[DeltaManager] Incremental recovery - applying', recoveryData.deltas.length, 'deltas');
        
        // Sort deltas by sequence number (broadcast events have sequenceNumber in meta)
        const sortedDeltas = recoveryData.deltas.sort((a, b) => {
          const aSeq = a.meta?.sequenceNumber || a.sequenceNumber || 0;
          const bSeq = b.meta?.sequenceNumber || b.sequenceNumber || 0;
          return aSeq - bSeq;
        });
        
        // Apply each delta in order
        for (const broadcastEvent of sortedDeltas) {
          // Broadcast events from API have sequenceNumber in meta
          const event = broadcastEvent as any; // API returns broadcast events, not DeltaPayload
          const sequenceNum = event.meta?.sequenceNumber || event.sequenceNumber;
          
          if (sequenceNum && sequenceNum > this.lastAppliedSequence) {
            // Convert broadcast event to DeltaPayload format
            const deltaPayload: DeltaPayload = {
              sequenceNumber: sequenceNum,
              entityType: event.entityType || event.entity || event.meta?.entityType,
              entityId: event.id || event.entityId || event.meta?.entityId,
              mutationType: event.mutationType || event.meta?.mutationType || 'update',
              timestamp: event.timestamp || event.meta?.timestamp || Date.now(),
              data: event.payload || event.data || event,
              meta: {
                ...event.meta,
                projectId: event.projectId || event.meta?.projectId,
                pulseId: event.pulseId || event.meta?.pulseId,
                taskId: event.taskId || event.meta?.taskId
              }
            };
            
            this.applyDelta(deltaPayload);
          }
        }
        
        // CONVERGENCE GUARD: some stored deltas are meta-only (no replayable
        // payload) and get skipped by the tree cache, which would leave
        // lastAppliedSequence behind the server sequence — every subsequent
        // live delta would then re-detect a gap and loop recovery forever.
        // If replay didn't reach the server's sequence, advance the cursor to
        // it and force a full tree refetch so the cache converges anyway.
        if (typeof recoveryData.sequenceNumber === 'number' &&
            this.lastAppliedSequence < recoveryData.sequenceNumber) {
          console.warn('[DeltaManager] Incremental replay did not converge', {
            applied: this.lastAppliedSequence,
            server: recoveryData.sequenceNumber,
          });
          this.lastAppliedSequence = recoveryData.sequenceNumber;
          this.stats.lastAppliedSequence = recoveryData.sequenceNumber;
          invalidateLinkedShareProjectionQueries(this.queryClient);
        }
        
        console.log(`[DeltaManager] ✓ Incremental recovery completed at sequence ${this.lastAppliedSequence}`);
      }
      // Fallback: Replace snapshot if available
      else if (recoveryData.snapshot) {
        console.log('[DeltaManager] No deltas available - replacing entire snapshot');
        
        // Find the active tree query key (handles parameterized keys)
        const activeQueryKey = this.findActiveTreeQueryKey(this.queryClient);
        
        if (activeQueryKey) {
          // Wrap snapshot in the same structure as normal queries
          this.queryClient.setQueryData(activeQueryKey, {
            snapshot: recoveryData.snapshot,
            sequenceNumber: recoveryData.sequenceNumber
          });
        } else {
          console.warn('[DeltaManager] No active tree query found - cannot update cache');
        }
        
        this.lastAppliedSequence = recoveryData.sequenceNumber;
        this.stats.lastAppliedSequence = recoveryData.sequenceNumber;
      }
      
      // Clear pending queue (deltas should now be applied)
      this.pendingQueue.clear();
      this.stats.pendingCount = 0;
      
      // Try to drain any remaining consecutive deltas
      this.drainConsecutivePendingDeltas();
      
      console.log('[DeltaManager] ✓ Gap recovery completed successfully');
    } finally {
      this.isSyncing = false;
      this.gapRecoveryInProgress = false;
    }
  }
  
  /**
   * Reset watchdog timer
   * Call this after successfully applying a delta
   */
  private resetWatchdog(): void {
    if (this.watchdogTimer !== null) {
      clearTimeout(this.watchdogTimer);
    }
    
    this.expectedNextSequence = this.lastAppliedSequence + 1;
    
    this.watchdogTimer = window.setTimeout(() => {
      this.onWatchdogTimeout();
    }, this.watchdogTimeout);
  }
  
  /**
   * Handle watchdog timeout
   * Called when no delta received within timeout period
   */
  private onWatchdogTimeout(): void {
    const timeSinceLastDelta = Date.now() - this.lastDeltaTimestamp;
    
    console.warn('[DeltaManager] ⏰ Watchdog timeout triggered:', {
      timeSinceLastDelta,
      expectedSequence: this.expectedNextSequence,
      lastApplied: this.lastAppliedSequence,
      pendingCount: this.pendingQueue.size
    });
    
    // If we're expecting a sequence and haven't received it, trigger recovery
    if (this.expectedNextSequence !== null && !this.gapRecoveryInProgress) {
      this.triggerGapRecovery(
        this.expectedNextSequence,
        this.expectedNextSequence
      );
    }
  }
  
  /**
   * Configure watchdog timeout
   */
  setWatchdogTimeout(timeoutMs: number): void {
    this.watchdogTimeout = timeoutMs;
    console.log('[DeltaManager] Watchdog timeout set to', timeoutMs, 'ms');
  }
  
  /**
   * Alert user of permanent sync failure
   */
  private alertUserOfSyncFailure(): void {
    console.error('[DeltaManager] ❌ PERMANENT SYNC FAILURE - Manual refresh required');
    
    // TODO: Show user toast or notification
    // This would integrate with the app's notification system
    if (typeof window !== 'undefined') {
      const shouldRefresh = confirm(
        'Real-time sync has failed. Please refresh the page to ensure you have the latest data.'
      );
      
      if (shouldRefresh) {
        window.location.reload();
      }
    }
  }
  
  /**
   * Get current statistics
   */
  getStats(): DeltaManagerStats {
    return {
      ...this.stats,
      pendingCount: this.pendingQueue.size,
    };
  }
  
  /**
   * Reset all state (useful for testing or reconnection)
   */
  reset(): void {
    console.log('[DeltaManager] Resetting state');
    
    if (this.watchdogTimer !== null) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    
    this.lastAppliedSequence = 0;
    this.pendingQueue.clear();
    this.isSyncing = false;
    this.gapRecoveryInProgress = false;
    this.expectedNextSequence = null;
    this.retryCount = 0;
    
    this.stats = {
      lastAppliedSequence: 0,
      pendingCount: 0,
      totalApplied: 0,
      gapsDetected: 0,
      duplicatesDropped: 0,
      recoveryAttempts: 0,
      lastRecoveryTimestamp: null,
    };
  }
  
  /**
   * Check if manager is currently syncing
   */
  isSynchronizing(): boolean {
    return this.isSyncing || this.gapRecoveryInProgress;
  }
  
  /**
   * Get pending queue status
   */
  getPendingQueue(): Array<{ seq: number; delta: DeltaPayload }> {
    return Array.from(this.pendingQueue.entries())
      .map(([seq, delta]) => ({ seq, delta }))
      .sort((a, b) => a.seq - b.seq);
  }
}

/**
 * Singleton instance
 */
export const deltaManager = new DeltaManager();
